import type React from "react";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import {
  type RecommendedTalent,
  useCreateFocusMatter,
  useFocusMatterRecommendations,
  useFocusMatters,
} from "../../../../hooks/use-focus-matters-api";
import { useCreatePipeline } from "../../../../hooks/use-sales-pipelines-api";
import {
  getAffiliationLabel,
  getEmploymentTypeLabel,
  getWorkStatusBadge,
} from "../../utils";
import { FocusMatterFormModal } from "./FocusMatterFormModal";

/**
 * ステータスのラベルとバッジクラスを返す
 * @param {string} status ステータス値
 * @return {object} label, badgeClass
 */
function getStatusBadge(status: string): {
  label: string;
  badgeClass: string;
} {
  switch (status) {
    case "open":
      return { label: "募集中", badgeClass: "badge-success" };
    case "filling":
      return { label: "候補あり", badgeClass: "badge-warning" };
    case "filled":
      return { label: "充足", badgeClass: "badge-info" };
    case "closed":
      return { label: "クローズ", badgeClass: "badge-ghost" };
    default:
      return { label: status, badgeClass: "badge-ghost" };
  }
}

/**
 * スコアバーを表示するコンポーネント
 * @param {object} props props
 * @param {string} props.label ラベル
 * @param {number} props.score スコア (0-1)
 * @return {React.ReactElement} スコアバー
 */
const ScoreBar: React.FC<{ label: string; score: number }> = ({
  label,
  score,
}) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-16 shrink-0 text-base-content/60">{label}</span>
    <progress
      className="progress progress-primary w-full"
      value={Math.round(score * 100)}
      max="100"
    />
    <span className="w-10 text-right">{Math.round(score * 100)}%</span>
  </div>
);

/**
 * おすすめタレントカードコンポーネント
 * @param {object} props props
 * @param {RecommendedTalent} props.talent タレント
 * @param {string} props.focusMatterId 注力案件ID
 * @param {Function} props.onAdded パイプライン追加後コールバック
 * @return {React.ReactElement} タレントカード
 */
const RecommendationCard: React.FC<{
  talent: RecommendedTalent;
  focusMatterId: string;
  onAdded: () => void;
}> = ({ talent, focusMatterId, onAdded }) => {
  const { createPipeline, loading: adding } = useCreatePipeline();
  const workStatus = getWorkStatusBadge(talent.workStatus);

  const handleAddToPipeline = useCallback(async () => {
    try {
      await createPipeline({
        talentId: talent.talentId,
        focusMatterId,
        opportunitySource: "focus_matter",
        stage: "preparation",
      });
      toast.success(`${talent.talentName} をパイプラインに追加しました`);
      onAdded();
    } catch {
      toast.error("パイプラインへの追加に失敗しました");
    }
  }, [
    createPipeline,
    talent.talentId,
    talent.talentName,
    focusMatterId,
    onAdded,
  ]);

  return (
    <div className="card bg-base-200 shadow-sm">
      <div className="card-body p-3 gap-2">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <span className="font-medium text-sm">{talent.talentName}</span>
            <span className="text-xs text-base-content/60 ml-2">
              {getAffiliationLabel(talent.affiliation)} /{" "}
              {getEmploymentTypeLabel(talent.employmentType)}
            </span>
          </div>
          <span className={`badge badge-sm ${workStatus.className}`}>
            {workStatus.label}
          </span>
        </div>

        {/* Skills */}
        {talent.skills.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {talent.skills.map(skill => (
              <span key={skill} className="badge badge-outline badge-xs">
                {skill}
              </span>
            ))}
          </div>
        )}

        {/* Desired reward */}
        {talent.desiredReward != null && (
          <div className="text-xs text-base-content/60">
            希望単価: {talent.desiredReward.toLocaleString()}万円
          </div>
        )}

        {/* Scores */}
        <div className="space-y-1 mt-1">
          <ScoreBar label="総合" score={talent.totalScore} />
          <ScoreBar label="スキル" score={talent.skillScore} />
          <ScoreBar label="単価" score={talent.rewardScore} />
          <ScoreBar label="勤務地" score={talent.locationScore} />
          <ScoreBar label="稼働" score={talent.availabilityScore} />
        </div>

        {/* Action */}
        <div className="card-actions justify-end mt-1">
          <button
            type="button"
            className="btn btn-primary btn-xs"
            disabled={adding}
            onClick={handleAddToPipeline}
          >
            {adding ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              "パイプラインに追加"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * おすすめタレントパネルコンポーネント
 * @param {object} props props
 * @param {string} props.matterId 注力案件ID
 * @return {React.ReactElement} おすすめタレントパネル
 */
const RecommendationPanel: React.FC<{ matterId: string }> = ({ matterId }) => {
  const { recommendations, loading, error, refetch } =
    useFocusMatterRecommendations(matterId);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <span className="loading loading-spinner loading-sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error alert-sm">
        <span>おすすめタレントの取得に失敗しました</span>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="text-center text-base-content/60 text-sm py-4">
        おすすめタレントが見つかりませんでした
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {recommendations.map(talent => (
        <RecommendationCard
          key={talent.talentId}
          talent={talent}
          focusMatterId={matterId}
          onAdded={refetch}
        />
      ))}
    </div>
  );
};

/**
 * 注力案件タブコンポーネント
 * 注力案件をカードグリッドで表示する
 * @return {React.ReactElement} 注力案件タブ
 */
export const FocusMatterTab: React.FC = () => {
  const { matters, loading, error, refetch } = useFocusMatters();
  const { createMatter, loading: creating } = useCreateFocusMatter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);

  const handleCreate = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        await createMatter(data as never);
        toast.success("注力案件を追加しました");
        setIsModalOpen(false);
        await refetch();
      } catch {
        toast.error("注力案件の追加に失敗しました");
      }
    },
    [createMatter, refetch],
  );

  if (error) {
    return (
      <div className="alert alert-error">
        <span>データの取得に失敗しました: {error.message}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-base-content/60">
          {matters.length}件の注力案件
        </span>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => setIsModalOpen(true)}
        >
          注力案件を追加
        </button>
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matters.map(matter => {
          const statusBadge = getStatusBadge(matter.status);
          const isExpanded = selectedMatterId === matter.id;
          return (
            <div
              key={matter.id}
              className={`card bg-base-100 shadow-sm cursor-pointer transition-all ${isExpanded ? "col-span-full ring-2 ring-primary/30" : ""}`}
            >
              <div
                className="card-body p-4"
                role="button"
                tabIndex={0}
                onClick={() =>
                  setSelectedMatterId(isExpanded ? null : matter.id)
                }
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedMatterId(isExpanded ? null : matter.id);
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <h3 className="card-title text-sm">{matter.title}</h3>
                  <span className={`badge badge-sm ${statusBadge.badgeClass}`}>
                    {statusBadge.label}
                  </span>
                </div>

                {matter.clientCompany && (
                  <p className="text-xs text-base-content/60">
                    {matter.clientCompany}
                  </p>
                )}

                {/* Reward range */}
                <div className="text-sm">
                  {matter.rewardMin != null || matter.rewardMax != null ? (
                    <span>
                      {matter.rewardMin != null
                        ? `${matter.rewardMin.toLocaleString()}円`
                        : ""}
                      {matter.rewardMin != null && matter.rewardMax != null
                        ? " ~ "
                        : ""}
                      {matter.rewardMax != null
                        ? `${matter.rewardMax.toLocaleString()}円`
                        : ""}
                    </span>
                  ) : (
                    <span className="text-base-content/60">単価未定</span>
                  )}
                </div>

                {/* Deadline */}
                {matter.deadline && (
                  <div className="text-xs text-base-content/60">
                    期限: {matter.deadline}
                  </div>
                )}

                {/* Required skills */}
                {matter.requiredSkills && matter.requiredSkills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {matter.requiredSkills.map(skill => (
                      <span
                        key={skill}
                        className="badge badge-outline badge-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                {/* Candidate count */}
                <div className="mt-2 text-xs text-base-content/60">
                  候補者: {matter.candidateCount}名
                </div>
              </div>

              {/* AI recommendations (expanded) */}
              {isExpanded && (
                <div className="border-t border-base-300 p-4">
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-1">
                    <span>AIおすすめタレント</span>
                  </h4>
                  <RecommendationPanel matterId={matter.id} />
                </div>
              )}
            </div>
          );
        })}

        {matters.length === 0 && (
          <div className="col-span-full text-center text-base-content/60 py-8">
            注力案件がありません
          </div>
        )}
      </div>

      {/* Create modal */}
      <FocusMatterFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        submitting={creating}
      />
    </div>
  );
};
